import type { CreatePortDto, PortDto, PortListDto, UpdatePortDto, WeatherResponse } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { CurrencyDto } from '../currencies/models';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';

@Injectable({
  providedIn: 'root',
})
export class PortService {
  apiName = 'Default';
  

  create = (input: CreatePortDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortDto>({
      method: 'POST',
      url: '/api/app/port',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/port/${id}`,
    },
    { apiName: this.apiName,...config });
  

  fetchWeatherInformationByIdAndUtcEndDate = (id: string, utcEndDate: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, WeatherResponse>({
      method: 'POST',
      url: `/api/app/port/${id}/fetch-weather-information`,
      params: { utcEndDate },
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortDto>({
      method: 'GET',
      url: `/api/app/port/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/port/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getCurrencyByPortId = (portId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CurrencyDto>({
      method: 'GET',
      url: `/api/app/port/currency/${portId}`,
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      url: '/api/app/port/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortDto[]>({
      method: 'GET',
      url: `/api/app/port/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      url: `/api/app/port/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortListDto[]>({
      method: 'GET',
      url: '/api/app/port/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: '/api/app/port/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<PortDto>>({
      method: 'GET',
      url: '/api/app/port',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<PortListDto>>({
      method: 'GET',
      url: '/api/app/port/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/port/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      url: '/api/app/port/schema',
    },
    { apiName: this.apiName,...config });
  

  getTimeZones = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: '/api/app/port/time-zones',
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortDto>({
      method: 'GET',
      url: `/api/app/port/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/port/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdatePortDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PortDto>({
      method: 'PUT',
      url: `/api/app/port/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
